import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";
import { coerceCapsuleId, sameCapsuleId } from "../../utils/capsuleId";
// v4 hardening: import submit thunks from the dashboard slice so that
// `state.capsuleUsers` (owned by this user slice) is updated deterministically
// when these thunks fulfill, not just when callers remember to dispatch
// `updateCapsuleUser(...)` after each one. This closes the F4.3-class
// regression risk where a new caller could forget the manual update and
// silently break the v4 stepper cascade. Caller-side dispatches are left in
// place intentionally as defense in depth (the merge is idempotent).
import {
  fetchSubmitLevelDeliverable,
  fetchSubmitMilestoneFeedback,
} from "../dashboard/slice";
import {
  clearContextChoice,
  readContextChoice,
  recordDeliberateReload,
  writeContextChoice,
} from "./contextChoice";
import {
  isBoundaryMission,
  statedBoundaryCapsuleIdOf,
} from "./missionBoundary";

const initialState = {
  isCohortUser: false,
  user: null,
  status: 0,
  mission: null,
  avatars: null,
  capsuleUsers: null,
  isCompletedReadObjectiveStep: false,
  isCompletedLeveldeliverableOfLastLevel: false,
  // The level advance's repair pin, session-only (both fields are stripped at
  // the persistence boundary by a transform in store/index.js. See the comment
  // on advanceCredentialLevel below for why they must never survive a reload).
  // It was `credentialAdvancePin` until the W4-F2.10 fix batch, when the stock
  // advance started writing it too; the mechanism was always about a mission
  // response rooted at the level being left, which is not a credential fact.
  missionAdvancePin: null,
  latestMissionRequestId: null,
  // M2 delivery context. The context generation the newest mission request
  // was issued under, so a reply issued before a context change is dropped
  // even when it is still the newest request. Session-only (store/index.js).
  latestMissionGeneration: null,
  // The last mission read failure FOR THE ACTIVE CONTEXT: { status, code,
  // squadId }. Session-only. A failure for any other context is never kept.
  missionError: null,
  // M2: which delivery the learner is acting in. The ONE authority for the
  // active squad and cohort; `user.squads[0]` and the singular `cohortUser`
  // never seed it. Session-only: rediscovered on every load.
  experienceContext: initialExperienceContext(),
  // Experience switcher (B1): the active context's own challenge, read only
  // when `/me`'s singular enrollment names a different cohort. Session-only:
  // { squadId, challengeId, status, requestId, data }.
  activeChallenge: null,
};

/**
 * THE DISCOVERY STATE, AS A FUNCTION SO EVERY RESET GETS A FRESH OBJECT.
 *
 * status: idle (never asked) -> loading -> ready | failed. A failed discovery
 * is a retryable state and is NEVER read as zero contexts.
 * active: set only by `resolveContextSelection` on an accepted discovery (a
 * valid stored choice, an in-memory choice, or the single-context auto-select)
 * or by `contextChosen`. Two or more with none chosen leaves it null and the
 * gate shows the chooser.
 * generation: bumps whenever `active` changes, so any in-flight mission read
 * issued for the previous context is dropped when it lands.
 * rediscoveredSquadId: the squad a 404 has already spent its one rediscovery
 * on, so a second 404 for the same squad settles on unavailable.
 * userId: the signed-in user the list was discovered for. Another user never
 * inherits it (the owner resets and rediscovers).
 * chosenSquadId: an explicit choice applied in memory, only when the browser
 * blocked storing it (no reload then). See `resolveContextSelection`.
 * A discovery 409 (`context_unavailable`) settles status "unavailable".
 */
export function initialExperienceContext() {
  return {
    status: "idle",
    contexts: [],
    active: null,
    generation: 0,
    requestId: null,
    error: null,
    rediscoveryRequested: false,
    rediscoveredSquadId: null,
    userId: null,
    chosenSquadId: null,
    // Counts the discovery replies the slice ACCEPTED (a fulfilled reply for
    // the newest request, or a 409). A stale or superseded reply never moves
    // it, so the owner reacts to accepted settles only.
    settleCount: 0,
  };
}

const CONTEXT_ID_FIELDS = [
  "cohort_id",
  "program_id",
  "squad_id",
  "cohort_user_id",
  "challenge_id",
];

const idOrNull = (value) => {
  const n = Number(value);
  return value !== null &&
    value !== undefined &&
    Number.isSafeInteger(n) &&
    n > 0
    ? n
    : null;
};

const LABEL_TEXT_FIELDS = [
  "challenge_title",
  "partner_name",
  "program_name",
  "cohort_title",
  "squad_name",
];
const LABEL_DATE_FIELDS = ["season_start", "season_end"];

const textOrNull = (value) =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;
const ymdOrNull = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;

/**
 * The discovery display labels (backend #200), kept with the ids. Every one
 * may be null. A context served without labels keeps none (undefined), so a
 * reader falls back exactly as it does for a null label.
 */
const normalizeLabels = (labels) => {
  if (!labels || typeof labels !== "object") return undefined;
  const out = {};
  LABEL_TEXT_FIELDS.forEach((field) => {
    out[field] = textOrNull(labels[field]);
  });
  LABEL_DATE_FIELDS.forEach((field) => {
    out[field] = ymdOrNull(labels[field]);
  });
  return out;
};

/** One context's ids, coerced, so "9" and 9 are the same squad. */
const normalizeContext = (context) => {
  if (!context || typeof context !== "object") return null;
  const out = {};
  CONTEXT_ID_FIELDS.forEach((field) => {
    out[field] = idOrNull(context[field]);
  });
  const labels = normalizeLabels(context.labels);
  if (labels) out.labels = labels;
  return out.squad_id && out.cohort_id ? out : null;
};

/* Identity is the ids only: a label that changes between two discoveries is
   a display change, never a context change (no reload, no generation). */
const sameContext = (a, b) =>
  !!a && !!b && CONTEXT_ID_FIELDS.every((field) => a[field] === b[field]);

/** The cohort `/me`'s singular enrollment names, or null. */
const meEnrollmentCohortId = (me) =>
  idOrNull(me?.cohortUser?.cohort?.id ?? me?.cohortUser?.cohort_id);

const normalizeContextList = (raw) =>
  (Array.isArray(raw) ? raw : []).map(normalizeContext).filter(Boolean);

/**
 * THE ONE SELECTION FUNCTION (experience switcher, r2 B2). Given the
 * normalized discovered list, in this order:
 *
 *   1. a valid stored choice: the tab's stored squad, when it is still one of
 *      the contexts AND still in the cohort it was chosen in (a duplicated
 *      tab inherits storage, so it is always revalidated here);
 *   2. an explicit choice held in memory (only when storage was blocked);
 *   3. the M2 auto-select: exactly one context and no selection required.
 *
 * Anything else selects none. The reducer and the thunk's `settledSquadId`
 * both call this, so they cannot disagree.
 */
export const resolveContextSelection = (
  contexts,
  { storedChoice = null, chosenSquadId = null, selectionRequired = false } = {},
) => {
  const list = Array.isArray(contexts) ? contexts : [];
  if (storedChoice) {
    const stored = list.find(
      (context) =>
        context.squad_id === storedChoice.squadId &&
        context.cohort_id === storedChoice.cohortId,
    );
    if (stored) return stored;
  }
  const chosenId = idOrNull(chosenSquadId);
  if (chosenId !== null) {
    const chosen = list.find((context) => context.squad_id === chosenId);
    if (chosen) return chosen;
  }
  if (list.length === 1 && selectionRequired !== true) return list[0];
  return null;
};

/** The error a rejected request carries, keeping status and `data.code`. */
const describeRequestError = (error) => ({
  status: error?.response?.status ?? null,
  code: error?.response?.data?.data?.code ?? null,
  message: error?.response?.data?.message ?? error?.message ?? null,
});

/**
 * Applies a new active context. A change bumps the generation and clears
 * everything derived from the previous context: the mission, the advance pin,
 * the optimistic task flag that lives inside the mission, and the previous
 * context's mission error.
 *
 * The mission survives only when it was read for the new active squad. A
 * mission with no `context` (the legacy read) has no proven owner and is
 * dropped; the owner reads the scoped mission as soon as discovery settles.
 */
const applyActiveContext = (state, next) => {
  const ec = state.experienceContext;
  const previous = ec.active;
  if (sameContext(previous, next) || (!previous && !next)) {
    ec.active = next;
    if (!next) state.mission = null;
    return;
  }
  ec.active = next;
  ec.generation += 1;
  state.missionAdvancePin = null;
  state.missionError = null;
  state.activeChallenge = null;
  const missionSquad = idOrNull(state.mission?.context?.squad_id);
  if (!next || missionSquad !== next.squad_id) state.mission = null;
};

export const fetchUser = createAsyncThunk("user/fetchUser", async () => {
  const response = await axiosInstance(
    "/users/me?include=profile,cohortUser.cohort.challenges.partner,squads.activities,squads.announcements,squads,squads.collections.bookmarks,events,lessons,schools,squads.quests.user,squads.squadMilestones.quests.user",
    "get",
  );
  return response.data.data;
});

export const fetchAvatars = createAsyncThunk("user/fetchAvatars", async () => {
  const response = await axiosInstance("/avatars", "get");
  return response.data.data;
});

/**
 * M2: the own-person delivery context list (`GET /users/experience-contexts`).
 * The Layout-level owner is its one routine caller; Season details also awaits
 * it right after a join. A response that is not a context list is a failure,
 * never zero contexts. `userId` names the signed-in user it is read for.
 */
export const fetchExperienceContexts = createAsyncThunk(
  "user/fetchExperienceContexts",
  async (arg, { getState, rejectWithValue, requestId }) => {
    // A newer discovery started after this one: this reply is superseded.
    // The reducers below already ignore it by request id; the flag tells the
    // slices that match on this action without importing this slice (tasks,
    // planTasks) to ignore it too, so it never clears their state.
    const superseded = () => {
      const current = getState()?.user?.experienceContext?.requestId;
      // No user slice at all (a partial test store): nothing to compare.
      return current !== undefined && current !== requestId;
    };
    try {
      const response = await axiosInstance("/users/experience-contexts", "get");
      const data = response?.data?.data;
      if (!data || !Array.isArray(data.contexts)) {
        return rejectWithValue({
          status: null,
          code: "malformed_contexts",
          message: "Experience contexts response was malformed.",
          superseded: superseded(),
        });
      }
      // The tab's stored choice, revalidated against THIS list by the one
      // selection function. A stored choice the list no longer names is
      // forgotten, so the learner sees the chooser instead of a dead squad.
      const userId = idOrNull(arg?.userId);
      const storedChoice = readContextChoice(userId);
      const contexts = normalizeContextList(data.contexts);
      const selected = resolveContextSelection(contexts, {
        storedChoice,
        chosenSquadId: getState()?.user?.experienceContext?.chosenSquadId,
        selectionRequired: data.selection_required === true,
      });
      if (
        storedChoice &&
        !superseded() &&
        (!selected || selected.squad_id !== storedChoice.squadId)
      ) {
        clearContextChoice(userId);
      }
      // The squad this list selects (or null), carried on the action so
      // the slices holding one context's state (tasks, planTasks) can tell a
      // context change without importing this slice.
      return {
        ...data,
        storedChoice,
        settledSquadId: selected?.squad_id ?? null,
        superseded: superseded(),
      };
    } catch (error) {
      return rejectWithValue({
        ...describeRequestError(error),
        superseded: superseded(),
      });
    }
  },
);

/**
 * The mission for ONE explicit delivery context
 * (`GET /users/mission-context?squad_id=`). Every caller passes the squad it
 * captured at request start and skips the dispatch when no context is
 * selected. A missing or malformed squad is refused by `condition` before
 * any action or request, so it cannot supersede a real read in flight.
 *
 * A rejection keeps the HTTP status and the server's `data.code`, and the
 * rejected reducer below never throws.
 */
/**
 * EXPERIENCE SWITCHER (B1): THE ACTIVE CONTEXT'S OWN CHALLENGE.
 *
 * `/me` carries ONE enrollment, so for a learner in two cohorts it always
 * names one of them. When it names a cohort other than the active context's,
 * that context's enrollment is built from its own mission reply (the
 * `cohortUser` row) plus this one read of its challenge, in the same
 * ChallengeResource shape `/me` nests at `cohort.challenges[0]`. `partner` is
 * the only include, and it is on the live allowlist for
 * `ChallengeAPIController@show` (H1b). Replaced on fulfil only while the
 * active context still names that squad and challenge.
 *
 * It replaced M2's D3 `/me` refresh: a learner whose `/me` names another
 * cohort is no longer unavailable, it reads its own context's challenge.
 */
export const fetchActiveChallenge = createAsyncThunk(
  "user/fetchActiveChallenge",
  async ({ challengeId }, { rejectWithValue }) => {
    const id = idOrNull(challengeId);
    try {
      const response = await axiosInstance(
        `/challenges/${id}?include=partner`,
        "get",
      );
      const challenge = response?.data?.data;
      if (!challenge || idOrNull(challenge.id) !== id) {
        return rejectWithValue({
          status: null,
          code: "malformed_challenge",
          message: "Challenge response was malformed.",
        });
      }
      return challenge;
    } catch (error) {
      return rejectWithValue(describeRequestError(error));
    }
  },
  {
    condition: (arg) =>
      idOrNull(arg?.challengeId) !== null && idOrNull(arg?.squadId) !== null,
  },
);

export const fetchMission = createAsyncThunk(
  "user/fetchMission",
  async ({ squadId }, { rejectWithValue }) => {
    const id = idOrNull(squadId);
    try {
      const response = await axiosInstance(
        `/users/mission-context?squad_id=${id}`,
        "get",
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue({ ...describeRequestError(error), squadId: id });
    }
  },
  { condition: (arg) => idOrNull(arg?.squadId) !== null },
);

/**
 * Whether a mission reply (fulfilled or rejected) may touch the current
 * state. Dropped when the context changed after the request was issued, when
 * it was requested for a squad other than the active one, or when the server
 * says it describes another squad. A context-scoped reply with no active
 * context is dropped too: the context it was read for is gone.
 */
/**
 * MAY A LESSONLESS ADVANCE TO `toCapsuleId` BE RECORDED? (production,
 * 2026-10-12.)
 *
 * Only at a level boundary, only to the capsule the payload states as the
 * position (the pin is read only when it names that capsule, so any other
 * destination would be a pin nothing reads), and never to the capsule the
 * learner already stands on. That last one is the advance-to-self guard: once
 * the pin names the stated capsule the learner IS there, and a second
 * terminal on that level must not advance it again.
 *
 * @param {object} state the user slice
 * @param {number|string|null|undefined} toCapsuleId
 * @returns {boolean}
 */
const boundaryAdvanceApplies = (state, toCapsuleId) => {
  if (!isBoundaryMission(state.mission)) return false;
  if (!sameCapsuleId(toCapsuleId, statedBoundaryCapsuleIdOf(state.mission))) {
    return false;
  }
  return !sameCapsuleId(state.missionAdvancePin?.to, toCapsuleId);
};

const missionReplyBelongsToActiveContext = (state, action) => {
  const ec = state.experienceContext;
  if (
    state.latestMissionGeneration !== null &&
    state.latestMissionGeneration !== ec.generation
  ) {
    return false;
  }
  const argSquad = idOrNull(action.meta?.arg?.squadId);
  const replySquad = idOrNull(action.payload?.context?.squad_id);
  const activeSquad = ec.active?.squad_id ?? null;
  if (activeSquad === null) return argSquad === null && replySquad === null;
  if (argSquad !== null && argSquad !== activeSquad) return false;
  if (replySquad !== null && replySquad !== activeSquad) return false;
  return true;
};

export const fetchUpdateLevelObjective = createAsyncThunk(
  "dashboard/fetchUpdateLevelObjective",
  async (data) => {
    const response = await axiosInstance("/capsule-users", "post", data);
    return response.data.data;
  },
);

export const addedCapsuleUser = createAsyncThunk(
  "dashboard/addedCapsuleUser",
  async (data) => {
    const response = await axiosInstance("/capsule-users", "post", data);
    return response.data.data;
  },
);

export const fetchUpdateUser = createAsyncThunk(
  "user/fetchUpdateUser",
  async (data) => {
    const { id, ...userData } = data;
    const response = await axiosInstance(`/users/${id}`, "put", userData);
    return response.data.data;
  },
);

export const fetchCapsuleUser = createAsyncThunk(
  "user/fetchCapsuleUser",
  async (id) => {
    const response = await axiosInstance(`/capsule-users?user_id=${id}`, "get");
    return response.data.data;
  },
);

export const fetchUploadSquadPhoto = createAsyncThunk(
  "user/fetchUploadSquadPhoto",
  async ({ file, squad_id, school_id }) => {
    const formData = new FormData();
    formData.append("logo_url", file);
    const response = await axiosInstance(
      `/squads/${squad_id}/?school_id=${school_id}`,
      "put",
      formData,
      {
        "Content-Type": "multipart/form-data",
      },
    );
    return response.data.data;
  },
);

export const fetchCurrentLevel = createAsyncThunk(
  "user/fetchCurrentLevel",
  async (capsuleId) => {
    const response = await axiosInstance(`/capsules/${capsuleId}`, "get");
    return response.data.data.week;
  },
);

export const fetchWSAuth = createAsyncThunk("user/fetchWSAuth", async () => {
  const response = await axiosInstance("/socket/auth", "get");
  return response.data;
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setIsCohortUser: (state, action) => {
      state.isCohortUser = action.payload;
    },
    updateCohortUser: (state, action) => {
      // Experience switcher (B1): an enrollment write for the active context
      // when `/me` names another cohort belongs to the active context's own
      // enrollment (its mission's `cohortUser`), never to `/me`'s, which
      // describes the other experience.
      const payloadCohort = idOrNull(
        action.payload?.cohort_id ?? action.payload?.cohort?.id,
      );
      const active = state.experienceContext.active;
      if (
        payloadCohort !== null &&
        active &&
        payloadCohort === active.cohort_id &&
        meEnrollmentCohortId(state.user) !== null &&
        meEnrollmentCohortId(state.user) !== payloadCohort
      ) {
        if (
          state.mission?.cohortUser &&
          idOrNull(state.mission?.context?.squad_id) === active.squad_id
        ) {
          const fields = { ...action.payload };
          delete fields.cohort;
          state.mission.cohortUser = { ...state.mission.cohortUser, ...fields };
        }
        return;
      }
      state.user.cohortUser = {
        ...state.user.cohortUser,
        ...action.payload,
        cohort: {
          ...state.user.cohortUser?.cohort,
          ...action.payload.cohort,
          challenges:
            action.payload.cohort?.challenges ||
            state.user.cohortUser?.cohort?.challenges,
        },
      };
    },
    isTaskCreated: (state, action) => {
      // M2: a context change can clear the mission under a pending caller.
      if (!state.mission) return;
      state.mission.isTaskCreated = action.payload;
    },
    setNextStatus: (state) => {
      state.status += 1;
    },
    setPreviousStatus: (state) => {
      state.status -= 1;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    /**
     * updateMissionCapsuleId. The stock level advance.
     *
     * IT NOW PINS THE TRANSITION IT PERFORMED (W4-F2.10 fix batch, MAJOR 1).
     * Every effective advance is followed by a mission read, because the level
     * the learner lands on has to be described by a payload fetched AFTER the
     * ship rather than before it. `UserAPIController::getMission` derives
     * `lesson` from the learner's newest `user_lessons` row, and shipping a
     * deliverable creates none, so that read comes back rooted at the level
     * just closed. Without a pin it would therefore UNDO the advance, the
     * cascade would re-derive, advance again, read again, and never settle.
     * With one, the fulfilment repair re-applies the transition to the fresh
     * payload and the read costs nothing but freshness.
     *
     * `consumeNextCapsule: false` is the difference from the credential path
     * below, and it is this reducer's existing behaviour written down: the
     * stock advance leaves `nextCapsule` in place and relies on being
     * idempotent. Nulling it here would make `hasNextCapsule` false on the
     * landed level and route its terminal through the final-level flag.
     */
    updateMissionCapsuleId: (state) => {
      // M2: a context change can clear the mission under a pending caller.
      if (!state.mission) return;
      /* A LEVEL BOUNDARY CARRIES NO LESSON TO MOVE (production, 2026-10-12).
         `/users/mission-context` omits the finished level's lesson and states
         the next capsule as `context.capsule_id`; `selectBoundaryCapsuleId`
         holds the learner on the previous level until this advance. So the
         advance is recorded as the pin, and a pin whose destination is the
         stated capsule is what moves them. `nextCapsule` stays exactly as the
         lesson branch leaves it. */
      if (!state.mission.lesson) {
        if (!boundaryAdvanceApplies(state, state.mission.nextCapsule?.id)) {
          return;
        }
        state.missionAdvancePin = {
          from: null,
          to: coerceCapsuleId(state.mission.nextCapsule.id),
          consumeNextCapsule: false,
        };
        /* The same task reset the lesson branch performs below, for the same
           reason: the level the learner lands on starts with no task. */
        state.mission.isTaskCreated = false;
        return;
      }
      const from = coerceCapsuleId(state.mission?.lesson?.capsule_id);
      const to = coerceCapsuleId(state.mission?.nextCapsule?.id);
      state.mission.lesson.capsule_id = state.mission.nextCapsule.id;
      /* A DEGENERATE ADVANCE PINS NOTHING. `nextCapsule` is left in place
         above, so a second terminal on the landed level would advance it to
         itself; a pin of {from: N, to: N} matches every payload and repairs
         none of them, and it would also discard a live pin from the real
         transition before it. */
      if (from !== null && to !== null && !sameCapsuleId(from, to)) {
        state.missionAdvancePin = { from, to, consumeNextCapsule: false };
      }
      // The level transition owns the task-state reset (credential
      // plan-progression hotfix, 2026-08-11). mission.isTaskCreated is
      // persisted and not level-scoped, so a newly entered capsule must start
      // with no task created. All three terminal advances in Dashboard.jsx
      // (stock v4, the legacy cascade, and the credential mirror) go through
      // this action,
      // so doing it here makes the reset atomic with the advance instead of
      // inferred afterwards from a field this reducer has already overwritten.
      // For stock cohorts this is a harmless duplicate of v4 gate 1's own
      // reset, which is unchanged.
      state.mission.isTaskCreated = false;
    },
    /**
     * advanceCredentialLevel. The credential path's level advance, as an
     * atomic compare-and-set (codex round-1 finding 1).
     *
     * `updateMissionCapsuleId` above reads `nextCapsule` and leaves it in place,
     * which is fine for the stock path (it retries idempotently and reloads
     * often) but wrong here: with `nextCapsule` still pointing at N+1 after the
     * advance, level N+1 could later "advance" to itself and the final-level
     * flag would then be unreachable forever. So this consumes it.
     *
     * The compare-and-set is what makes a stale or duplicated dispatch free: the
     * moment captures its {from, to} pair when it OPENS, and by the time the
     * student clicks continue the mission may have been replaced underneath it.
     * A pair that no longer describes the current state does nothing at all.
     *
     * The stock path never dispatches this action, and this action is the only
     * writer of `missionAdvancePin`, so stock behaviour is unchanged.
     */
    advanceCredentialLevel: (state, action) => {
      const { fromCapsuleId, toCapsuleId } = action.payload || {};
      // The compare-and-set runs on the shared capsule-id contract, not on
      // strict equality. Ids cross a JSON boundary, so "502" and 502 are the
      // same capsule; comparing them raw meant a type-mismatched payload
      // produced a set that could never match, and the student's
      // acknowledgement silently did nothing.
      const from = coerceCapsuleId(fromCapsuleId);
      const to = coerceCapsuleId(toCapsuleId);
      if (from === null || to === null) return;
      /* THE SAME BOUNDARY `updateMissionCapsuleId` handles above: no lesson to
         compare or move. The set still requires the payload's `nextCapsule` to
         be the destination, and it is recorded as the pin, which consumes
         `nextCapsule` on a later read exactly as the lesson branch's does. */
      if (state.mission && !state.mission.lesson) {
        if (!sameCapsuleId(state.mission.nextCapsule?.id, to)) return;
        if (!boundaryAdvanceApplies(state, to)) return;
        state.mission.nextCapsule = null;
        state.mission.isTaskCreated = false;
        state.missionAdvancePin = { from, to, consumeNextCapsule: true };
        return;
      }
      if (!sameCapsuleId(state.mission?.lesson?.capsule_id, from)) return;
      if (!sameCapsuleId(state.mission?.nextCapsule?.id, to)) return;

      state.mission.lesson.capsule_id = to;
      state.mission.nextCapsule = null;
      // Same reset `updateMissionCapsuleId` performs, for the same reason
      // (credential plan-progression hotfix, 2026-08-11): isTaskCreated is
      // persisted and not level-scoped, so a newly entered capsule must start
      // with no task created or the plan gate opens pre-satisfied.
      state.mission.isTaskCreated = false;
      // The pin is what stops a mission response that was already in flight,
      // rooted at the OLD capsule, from silently undoing an advance the student
      // has already been shown. It is deliberately in-memory only: on a reload,
      // server truth is authoritative and a stale pin could pin a student to a
      // level they never actually reached.
      //
      // `consumeNextCapsule` is this path's own half. The advance above already
      // consumed `nextCapsule`, so a repaired payload has to consume it again
      // or level N+1 could advance to itself and the final-level flag would be
      // unreachable. The stock advance deliberately does NOT consume it, so it
      // pins `false`.
      state.missionAdvancePin = { from, to, consumeNextCapsule: true };
    },
    updateIsTaskCreated: (state, action) => {
      if (!state.mission) return;
      state.mission.isTaskCreated = action.payload;
    },
    updateCapsuleUser: (state, action) => {
      state.capsuleUsers = state.capsuleUsers.map((capsuleUser) =>
        capsuleUser.id === action.payload.id
          ? { ...capsuleUser, ...action.payload }
          : capsuleUser,
      );
    },
    setIsCompletedReadObjectiveStep: (state, action) => {
      state.isCompletedReadObjectiveStep = action.payload;
    },
    setIsCompletedLeveldeliverableOfLastLevel: (state, action) => {
      state.isCompletedLeveldeliverableOfLastLevel = action.payload;
    },
    /** Ask the Layout-level owner to run discovery again (after a join). */
    requestContextRediscovery: (state) => {
      state.experienceContext.rediscoveryRequested = true;
    },
    /**
     * Back to an undiscovered state, for a different signed-in user. The
     * generation still advances, so any reply issued for the previous user's
     * context is dropped when it lands.
     */
    /**
     * The owner's loop guard: the same context change already reloaded the
     * page moments ago, so instead of reloading again the learner sees the
     * plain unavailable state.
     */
    markContextUnavailable: (state) => {
      state.experienceContext.status = "unavailable";
      applyActiveContext(state, null);
    },
    /**
     * Experience switcher: an explicit choice applied IN MEMORY. `chooseContext`
     * dispatches it only when the browser blocked storing the choice and no
     * context is active yet, so nothing scoped to another context was loaded
     * and no reload is needed. Ignored for a squad that is not discovered.
     */
    contextChosen: (state, action) => {
      const ec = state.experienceContext;
      const squadId = idOrNull(action.payload);
      const chosen = ec.contexts.find(
        (context) => context.squad_id === squadId,
      );
      if (!chosen || ec.status !== "ready" || ec.active) return;
      ec.chosenSquadId = squadId;
      applyActiveContext(state, chosen);
    },
    resetExperienceContext: (state) => {
      const generation = state.experienceContext.generation + 1;
      state.experienceContext = { ...initialExperienceContext(), generation };
      state.mission = null;
      state.missionError = null;
      state.missionAdvancePin = null;
      state.activeChallenge = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isCohortUser = !!action.payload.cohortUser;
    });
    builder.addCase(fetchAvatars.fulfilled, (state, action) => {
      state.avatars = action.payload;
    });
    // Take-latest ordering guard (codex round-2 finding 5). `fetchMission` has
    // several callers and no ordering discipline of its own, so two requests can
    // be in flight at once and the OLDER one can land last. RTK stamps every
    // thunk action with `meta.requestId`; recording the newest here and checking
    // it on fulfilment means an out-of-order response can never overwrite a
    // newer one. This applies to stock and credential alike. Stock had the same
    // latent race, it just recovered from it by refetching.
    // M2 discovery. Only the newest discovery request may settle the state.
    builder.addCase(fetchExperienceContexts.pending, (state, action) => {
      const ec = state.experienceContext;
      ec.status = "loading";
      ec.requestId = action.meta?.requestId ?? null;
      ec.rediscoveryRequested = false;
      ec.userId = idOrNull(action.meta?.arg?.userId);
    });
    builder.addCase(fetchExperienceContexts.fulfilled, (state, action) => {
      const ec = state.experienceContext;
      if (action.meta?.requestId !== ec.requestId) return;
      ec.status = "ready";
      ec.error = null;
      ec.settleCount += 1;
      ec.contexts = normalizeContextList(action.payload.contexts);
      applyActiveContext(
        state,
        resolveContextSelection(ec.contexts, {
          storedChoice: action.payload.storedChoice ?? null,
          chosenSquadId: ec.chosenSquadId,
          selectionRequired: action.payload.selection_required === true,
        }),
      );
    });
    builder.addCase(fetchExperienceContexts.rejected, (state, action) => {
      const ec = state.experienceContext;
      if (action.meta?.requestId !== ec.requestId) return;
      ec.error = action.payload ?? { status: null, code: null, message: null };
      // A 409 is the server saying the learner's contexts are unavailable
      // (for example more than 100): unavailable, not a connection failure.
      if (action.payload?.status === 409) {
        ec.status = "unavailable";
        ec.settleCount += 1;
        applyActiveContext(state, null);
        return;
      }
      // Retryable, and never zero contexts: the list and the active context
      // are left exactly as they were; selectors read nothing while failed.
      ec.status = "failed";
    });
    // B1: the active context's own challenge. Only the newest read for the
    // squad and challenge the active context still names may settle it.
    builder.addCase(fetchActiveChallenge.pending, (state, action) => {
      state.activeChallenge = {
        squadId: idOrNull(action.meta?.arg?.squadId),
        challengeId: idOrNull(action.meta?.arg?.challengeId),
        status: "loading",
        requestId: action.meta?.requestId ?? null,
        data: null,
      };
    });
    // A context change nulls `activeChallenge` (applyActiveContext), so a
    // reply issued for a previous context finds nothing to settle, and a
    // newer read for the same context replaces the request id.
    const settleActiveChallenge = (status) => (state, action) => {
      const current = state.activeChallenge;
      if (!current || current.requestId !== action.meta?.requestId) return;
      current.status = status;
      current.data = status === "ready" ? action.payload : null;
    };
    builder.addCase(
      fetchActiveChallenge.fulfilled,
      settleActiveChallenge("ready"),
    );
    builder.addCase(
      fetchActiveChallenge.rejected,
      settleActiveChallenge("failed"),
    );
    builder.addCase(fetchMission.pending, (state, action) => {
      state.latestMissionRequestId = action.meta?.requestId ?? null;
      state.latestMissionGeneration = state.experienceContext.generation;
    });
    builder.addCase(fetchMission.fulfilled, (state, action) => {
      if (
        state.latestMissionRequestId != null &&
        action.meta?.requestId !== state.latestMissionRequestId
      ) {
        // A stale response. Drop it whole: touching state.mission at all is the
        // bug. (Null latestMissionRequestId means we have no ordering
        // opinion, e.g. the first fetch of a freshly rehydrated session, so
        // accept.)
        return;
      }
      // M2: a reply for a previous context cannot mutate the current one. It
      // is dropped when the context changed after the request was issued, when
      // it was requested for another squad, or when the server says it
      // describes another squad.
      if (!missionReplyBelongsToActiveContext(state, action)) return;
      state.mission = action.payload;
      state.missionError = null;

      // Advance-pin repair. The server legitimately keeps reporting the OLD
      // capsule until the student opens a lesson in the new one, so a payload
      // rooted at `pin.from` is not stale, it is just behind the client. Without
      // this, the first mission refetch after an advance would drop the student
      // back onto the level they just finished.
      const pin = state.missionAdvancePin;
      if (!pin) return;
      /* A BOUNDARY PAYLOAD HAS NO LESSON FOR THE REPAIR BELOW TO READ OR MOVE.
         The pin is kept, because it is what places the learner on the stated
         capsule (`selectBoundaryCapsuleId`), and the credential path's
         consumption of `nextCapsule` is re-applied. */
      if (!state.mission?.lesson) {
        if (
          pin.consumeNextCapsule === true &&
          sameCapsuleId(state.mission?.nextCapsule?.id, pin.to)
        ) {
          state.mission.nextCapsule = null;
        }
        return;
      }
      // Same capsule-id contract as the advance that wrote the pin: the pin
      // holds coerced ids, so a server payload that reports capsule_id as a
      // numeric string must still match it.
      const incomingCapsuleId = state.mission?.lesson?.capsule_id;
      if (sameCapsuleId(incomingCapsuleId, pin.to)) {
        // The server has caught up. Nothing left to repair.
        state.missionAdvancePin = null;
        return;
      }
      if (sameCapsuleId(incomingCapsuleId, pin.from)) {
        state.mission.lesson.capsule_id = pin.to;
        if (
          pin.consumeNextCapsule === true &&
          sameCapsuleId(state.mission.nextCapsule?.id, pin.to)
        ) {
          state.mission.nextCapsule = null;
        }
        // Re-apply the advance's task reset too: the payload's isTaskCreated is
        // rooted at the OLD level, where it is true, and carrying it into the
        // new level would open the plan gate pre-satisfied.
        state.mission.isTaskCreated = false;
      }
      // Any other rooting (a capsule unrelated to the pin) is left exactly as
      // the server sent it. The pin only ever repairs the one transition it
      // describes.
    });
    // Never throws. The failure is kept for the active context only:
    // 404 spends this squad's one rediscovery, 409 reads as unavailable, and
    // anything else is retryable (selectContextAvailability).
    builder.addCase(fetchMission.rejected, (state, action) => {
      if (
        state.latestMissionRequestId != null &&
        action.meta?.requestId !== state.latestMissionRequestId
      ) {
        return;
      }
      if (!missionReplyBelongsToActiveContext(state, action)) return;
      const payload = action.payload ?? {};
      const squadId = idOrNull(payload.squadId ?? action.meta?.arg?.squadId);
      state.missionError = {
        status: payload.status ?? null,
        code: payload.code ?? null,
        squadId,
      };
      const ec = state.experienceContext;
      if (
        payload.status === 404 &&
        squadId !== null &&
        ec.rediscoveredSquadId !== squadId
      ) {
        ec.rediscoveredSquadId = squadId;
        ec.rediscoveryRequested = true;
      }
    });
    builder.addCase(fetchUpdateLevelObjective.fulfilled, (state, action) => {
      if (!state.capsuleUsers || !action.payload?.id) return;
      state.capsuleUsers = state.capsuleUsers.map((capsuleUser) =>
        capsuleUser.id === action.payload.id
          ? { ...capsuleUser, ...action.payload }
          : capsuleUser,
      );
      state.isCompletedReadObjectiveStep = true;
    });
    builder.addCase(fetchUpdateUser.fulfilled, (state, action) => {
      state.user = { ...state.user, ...action.payload };
    });
    builder.addCase(fetchCapsuleUser.fulfilled, (state, action) => {
      state.capsuleUsers = action.payload;
    });
    builder.addCase(addedCapsuleUser.fulfilled, (state, action) => {
      state.capsuleUsers = [...state.capsuleUsers, action.payload];
    });
    // v4 hardening: Progress submit. Payload is the updated capsuleUser
    // returned from POST /capsule-users (sets milestone_report_submitted=1).
    // Mirrors fetchUpdateLevelObjective.fulfilled above. Match by id (the
    // canonical capsuleUser primary key returned by the backend). No-op if
    // capsuleUsers is null or the id is not found, preserving the legacy
    // caller-side dispatch as the source of truth in those edge cases.
    builder.addCase(fetchSubmitMilestoneFeedback.fulfilled, (state, action) => {
      if (!state.capsuleUsers || !action.payload?.id) return;
      state.capsuleUsers = state.capsuleUsers.map((capsuleUser) =>
        capsuleUser.id === action.payload.id
          ? { ...capsuleUser, ...action.payload }
          : capsuleUser,
      );
    });
    // v4 hardening: Deliverable submit. Payload is the updated capsuleUser
    // returned from POST /capsule-users (sets deliverables_submitted=1).
    // Same shape and matcher as the Progress reducer above.
    builder.addCase(fetchSubmitLevelDeliverable.fulfilled, (state, action) => {
      if (!state.capsuleUsers || !action.payload?.id) return;
      state.capsuleUsers = state.capsuleUsers.map((capsuleUser) =>
        capsuleUser.id === action.payload.id
          ? { ...capsuleUser, ...action.payload }
          : capsuleUser,
      );
    });
  },
});

export const {
  advanceCredentialLevel,
  setIsCohortUser,
  setNextStatus,
  setPreviousStatus,
  setStatus,
  isTaskCreated,
  contextChosen,
  markContextUnavailable,
  requestContextRediscovery,
  resetExperienceContext,
  updateCapsuleUser,
  updateMissionCapsuleId,
  updateCohortUser,
  updateIsTaskCreated,
  setIsCompletedReadObjectiveStep,
  setIsCompletedLeveldeliverableOfLastLevel,
} = userSlice.actions;

export default userSlice.reducer;

/** What `chooseContext` did. */
export const CHOOSE_OUTCOME = Object.freeze({
  // Stored for this tab; the page is reloading into the chosen experience.
  RELOADING: "reloading",
  // Storage was blocked and nothing was active yet: applied in memory.
  CHOSEN: "chosen",
  // Already the active experience: nothing to do.
  CURRENT: "current",
  // Storage was blocked while another experience is active: no reload, no
  // switch (a switch without the reload would mix two contexts' state).
  BLOCKED: "blocked",
  // Not one of the discovered contexts, or no signed-in user to scope it to.
  INVALID: "invalid",
});

/**
 * EXPERIENCE SWITCHER: THE LEARNER CHOOSES A CONTEXT (r2 B2 to B4).
 *
 * The choice is stored for this tab under a key scoped to the user, then the
 * page reloads, and the next discovery settle selects it through
 * `resolveContextSelection` (which revalidates it). The reload is M2's single
 * reload for a context change.
 *
 * THE LOOP-GUARD BYPASS IS THIS CLICK AND NOTHING ELSE. This path never reads
 * the 30-second guard, so a deliberate switch always goes through; it does
 * WRITE the reload record, so an automatic change that lands in the next 30
 * seconds is still held. Nothing is left behind for a restore or a later
 * rediscovery to spend. If the storage write fails there is no reload.
 */
export const chooseContext = (squadId) => (dispatch, getState) => {
  const ec = getState().user?.experienceContext;
  const id = idOrNull(squadId);
  const target =
    id === null
      ? null
      : (ec?.contexts?.find((context) => context.squad_id === id) ?? null);
  const userId = idOrNull(ec?.userId);
  if (!target || userId === null) return CHOOSE_OUTCOME.INVALID;
  const activeId = selectActiveSquadId(getState());
  if (activeId === id) return CHOOSE_OUTCOME.CURRENT;
  if (
    !writeContextChoice(userId, { squadId: id, cohortId: target.cohort_id })
  ) {
    if (ec.status === "ready" && !ec.active) {
      dispatch(contextChosen(id));
      return CHOOSE_OUTCOME.CHOSEN;
    }
    return CHOOSE_OUTCOME.BLOCKED;
  }
  recordDeliberateReload({ userId, from: activeId, to: id });
  window.location.reload();
  return CHOOSE_OUTCOME.RELOADING;
};

/* ===================== M2 DELIVERY CONTEXT SELECTORS =====================
 * The one place the active delivery is read from. `user.squads[0]` and the
 * singular `user.cohortUser` are history and never decide it; they are only
 * looked up BY the active context's ids for display fields.
 */

export const CONTEXT_AVAILABILITY = Object.freeze({
  // Discovery has not settled: show a skeleton, send nothing.
  PENDING: "pending",
  // Discovery failed: retryable, never read as zero contexts.
  FAILED: "failed",
  // Joined, but no single active context (zero, two or more, 409, or a 404
  // after its one rediscovery): plain unavailable, send nothing.
  UNAVAILABLE: "unavailable",
  // No enrollment at all: today's join experience.
  NOT_JOINED: "not_joined",
  // The active context's mission read failed and there is no mission for it
  // yet: retryable.
  MISSION_FAILED: "mission_failed",
  READY: "ready",
});

const selectExperienceContext = (state) =>
  state.user?.experienceContext ?? null;

/** The active context, only once discovery has settled on one. */
export const selectActiveContext = (state) => {
  const ec = selectExperienceContext(state);
  return ec?.status === "ready" ? (ec.active ?? null) : null;
};
export const selectActiveSquadId = (state) =>
  selectActiveContext(state)?.squad_id ?? null;
export const selectActiveCohortId = (state) =>
  selectActiveContext(state)?.cohort_id ?? null;
export const selectActiveProgramId = (state) =>
  selectActiveContext(state)?.program_id ?? null;
export const selectContextGeneration = (state) =>
  selectExperienceContext(state)?.generation ?? 0;
export const selectContextDiscoveryStatus = (state) =>
  selectExperienceContext(state)?.status ?? "idle";

/** The `/me` squad row whose id is the active squad, for display fields. */
export const selectActiveSquad = createSelector(
  [(state) => state.user?.user?.squads, selectActiveSquadId],
  (squads, squadId) =>
    squadId === null || !Array.isArray(squads)
      ? null
      : (squads.find((squad) => idOrNull(squad?.id) === squadId) ?? null),
);

/**
 * The active context's cohort when the `/me` enrollment names a different
 * one, else null. Then the active enrollment is built from the context's own
 * mission and challenge (B1), and `/me`'s enrollment describes nothing here.
 */
export const selectEnrollmentMismatchCohortId = (state) => {
  const active = selectActiveContext(state);
  if (!active) return null;
  const meCohort = meEnrollmentCohortId(state.user?.user);
  return meCohort !== null && meCohort !== active.cohort_id
    ? active.cohort_id
    : null;
};

/** The B1 build's progress for the active context. See ACTIVE_ENROLLMENT. */
export const ACTIVE_ENROLLMENT = Object.freeze({
  // `/me` names the active cohort (or names none): nothing to build.
  NOT_NEEDED: "not_needed",
  PENDING: "pending",
  READY: "ready",
  // The challenge read failed: retryable.
  FAILED: "failed",
  // The mission's enrollment is not the context's own `cohort_user_id`.
  REFUSED: "refused",
});

const selectActiveMissionCohortUser = (state) => {
  const active = selectActiveContext(state);
  const mission = state.user?.mission;
  if (!active || !mission) return null;
  return idOrNull(mission.context?.squad_id) === active.squad_id
    ? (mission.cohortUser ?? null)
    : null;
};

export const selectActiveEnrollmentStatus = (state) => {
  if (selectEnrollmentMismatchCohortId(state) === null) {
    return ACTIVE_ENROLLMENT.NOT_NEEDED;
  }
  const active = selectActiveContext(state);
  const challenge = state.user?.activeChallenge;
  if (active.challenge_id !== null) {
    const forActive =
      challenge &&
      challenge.squadId === active.squad_id &&
      challenge.challengeId === active.challenge_id;
    if (forActive && challenge.status === "failed") {
      return ACTIVE_ENROLLMENT.FAILED;
    }
  }
  const enrollment = selectActiveMissionCohortUser(state);
  if (!enrollment) return ACTIVE_ENROLLMENT.PENDING;
  // The availability check uses the context's OWN enrollment id from
  // discovery, never `/me`'s singular one.
  if (
    active.cohort_user_id === null ||
    idOrNull(enrollment.id) !== active.cohort_user_id
  ) {
    return ACTIVE_ENROLLMENT.REFUSED;
  }
  if (active.challenge_id === null) return ACTIVE_ENROLLMENT.READY;
  return challenge &&
    challenge.squadId === active.squad_id &&
    challenge.challengeId === active.challenge_id &&
    challenge.status === "ready"
    ? ACTIVE_ENROLLMENT.READY
    : ACTIVE_ENROLLMENT.PENDING;
};

/**
 * THE ACTIVE CONTEXT'S ENROLLMENT, in the `/me` `cohortUser` shape every
 * retained reader expects (`cohort.challenges[0]`, `read_challenge`).
 *
 *   - `/me` names the active cohort: `/me`'s own enrollment, unchanged;
 *   - `/me` names another cohort (a learner in two experiences, B1): the
 *     mission's own `cohortUser`, with a cohort holding the context's ids
 *     and its challenge from `fetchActiveChallenge`. A context whose
 *     challenge is ambiguous (`challenge_id` null, per M1) gets NO challenge;
 *     readers show their existing no-challenge state, never a guess;
 *   - otherwise null.
 */
export const selectActiveCohortUser = createSelector(
  [
    (state) => state.user?.user?.cohortUser,
    selectActiveContext,
    selectActiveMissionCohortUser,
    selectActiveEnrollmentStatus,
    (state) => state.user?.activeChallenge?.data ?? null,
  ],
  (meCohortUser, active, missionCohortUser, enrollmentStatus, challenge) => {
    if (!active) return null;
    if (enrollmentStatus === ACTIVE_ENROLLMENT.NOT_NEEDED) {
      if (!meCohortUser) return null;
      return meEnrollmentCohortId({ cohortUser: meCohortUser }) ===
        active.cohort_id
        ? meCohortUser
        : null;
    }
    if (enrollmentStatus !== ACTIVE_ENROLLMENT.READY) return null;
    return {
      ...missionCohortUser,
      cohort_id: active.cohort_id,
      cohort: {
        id: active.cohort_id,
        program_id: active.program_id,
        challenges:
          active.challenge_id !== null && challenge ? [challenge] : [],
      },
    };
  },
);

/**
 * THE DISPLAY-BASELINE IDENTITY (user, squad, challenge) that scopes the
 * level-marker and stats-pill baselines in localStorage. The squad and the
 * challenge are the ACTIVE context's, so two experiences never share a
 * baseline (no false or suppressed celebrations after a switch). Null until
 * all three are known.
 */
export const selectBaselineIdentityKey = (state) => {
  const userId = state.user?.user?.id;
  const squadId = selectActiveSquadId(state);
  // The context's own challenge id: discovery names it exactly, where the
  // enrollment's `challenges[0]` is only the first of the cohort's list.
  const challengeId = selectActiveContext(state)?.challenge_id ?? null;
  if (userId == null || squadId == null || challengeId == null) return null;
  return `${userId}_${squadId}_${challengeId}`;
};

/**
 * The discovered contexts a learner can switch between: two or more, from a
 * successful discovery (`contexts` is only ever written by one). Empty for a
 * single-context or zero-context learner, who sees no switcher at all.
 */
const EMPTY_LIST = Object.freeze([]);
export const selectSwitchableContexts = createSelector(
  [(state) => selectExperienceContext(state)?.contexts],
  (contexts) =>
    Array.isArray(contexts) && contexts.length >= 2 ? contexts : EMPTY_LIST,
);

/** What a mission-dependent surface may show. See CONTEXT_AVAILABILITY. */
export const selectContextAvailability = (state) => {
  const ec = selectExperienceContext(state);
  const status = ec?.status ?? "idle";
  if (status === "idle" || status === "loading") {
    return CONTEXT_AVAILABILITY.PENDING;
  }
  if (status === "failed") return CONTEXT_AVAILABILITY.FAILED;
  if (status === "unavailable") return CONTEXT_AVAILABILITY.UNAVAILABLE;
  if (ec.rediscoveryRequested) return CONTEXT_AVAILABILITY.PENDING;
  const active = ec.active;
  if (!active) {
    const joined =
      state.user?.isCohortUser === true || !!state.user?.user?.cohortUser;
    return joined
      ? CONTEXT_AVAILABILITY.UNAVAILABLE
      : CONTEXT_AVAILABILITY.NOT_JOINED;
  }
  const error = state.user?.missionError;
  if (error && error.squadId === active.squad_id) {
    if (error.status === 404 || error.status === 409) {
      return CONTEXT_AVAILABILITY.UNAVAILABLE;
    }
    const mission = state.user?.mission;
    const missionSquad = idOrNull(mission?.context?.squad_id);
    const hasMission =
      !!mission && (missionSquad === null || missionSquad === active.squad_id);
    if (!hasMission) return CONTEXT_AVAILABILITY.MISSION_FAILED;
  }
  // Experience switcher (B1, replacing M2's D3 rule): when `/me` names a
  // different cohort, the retained readers read the active context's own
  // enrollment instead, so the page waits for it (never mixing two cohorts)
  // rather than saying unavailable.
  switch (selectActiveEnrollmentStatus(state)) {
    case ACTIVE_ENROLLMENT.PENDING:
      return CONTEXT_AVAILABILITY.PENDING;
    case ACTIVE_ENROLLMENT.FAILED:
      return CONTEXT_AVAILABILITY.MISSION_FAILED;
    case ACTIVE_ENROLLMENT.REFUSED:
      return CONTEXT_AVAILABILITY.UNAVAILABLE;
    default:
      return CONTEXT_AVAILABILITY.READY;
  }
};
